import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Select, Input, Tag, Row, Col, Statistic, message, Space, Avatar, Tree, Tooltip } from 'antd';
import { ApartmentOutlined, TeamOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import apiService from 'services/api/apiService';

const deptColors = ['blue','green','orange','purple','cyan','magenta','red','gold','lime','geekblue'];

const OrgChartPage: React.FC = () => {
  const [orgData, setOrgData] = useState<any[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [editForm] = Form.useForm();
  const [view, setView] = useState<'list'|'tree'>('list');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [er, or] = await Promise.all([apiService.getEmployees(), apiService.getOrgChart()]);
      if (er.success) setEmployees(er.data || []);
      if (or.success) {
        setOrgData(or.data || []);
        // Build antd Tree data
        const buildTree = (nodes: any[]): any[] => nodes.map(n => ({
          key: n.id,
          title: (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" style={{ background: '#1890ff' }}>{n.first_name?.charAt(0)}</Avatar>
              <span><strong>{n.first_name} {n.last_name}</strong> — {n.position}</span>
              <Tag color="blue" style={{ fontSize: 10 }}>{n.department}</Tag>
              {n.job_level && <Tag color="purple" style={{ fontSize: 10 }}>L{n.job_level}</Tag>}
            </div>
          ),
          children: n.children?.length > 0 ? buildTree(n.children) : undefined,
        }));
        setTreeData(buildTree(or.tree || []));
      }
    } finally { setLoading(false); }
  };

  const handleEdit = (record: any) => {
    setSelected(record);
    editForm.setFieldsValue({ reports_to_id: record.reports_to_id, job_level: record.job_level, job_band: record.job_band, cost_center: record.cost_center });
    setEditModal(true);
  };

  const handleSave = async (values: any) => {
    const r = await apiService.updateOrgStructure({ ...values, employee_id: selected.id });
    if (r.success) { message.success('Org structure updated'); setEditModal(false); editForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  // Group by department
  const depts = Array.from(new Set(orgData.map((e: any) => e.department).filter(Boolean))) as string[];
  const deptColorMap: Record<string, string> = {};
  depts.forEach((d, i) => { deptColorMap[d] = deptColors[i % deptColors.length]; });

  return (
    <div style={{ padding: 24 }}>
      <h1>Organisation Chart</h1>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}><Card><Statistic title="Total Employees" value={orgData.length} prefix={<TeamOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Departments" value={depts.length} prefix={<ApartmentOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Active" value={orgData.filter(e => e.status === 'active').length} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Management Levels" value={Math.max(...orgData.map(e => e.job_level || 1), 1)} /></Card></Col>
      </Row>

      <Card title="Organisation Structure" extra={
        <Space>
          <Button onClick={() => setView(view === 'list' ? 'tree' : 'list')}>{view === 'list' ? '🌳 Tree View' : '📋 List View'}</Button>
        </Space>
      }>
        {view === 'tree' ? (
          <div style={{ padding: 16, minHeight: 400, overflowX: 'auto' }}>
            {treeData.length > 0 ? <Tree treeData={treeData} defaultExpandAll showLine selectable={false} /> : <p>No org structure defined yet. Add reporting lines in List View.</p>}
          </div>
        ) : (
          <Table dataSource={orgData} rowKey="id" loading={loading} pagination={{ pageSize: 15 }}
            columns={[
              { title: 'Employee', key: 'name', render: (_: any, r: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar style={{ background: deptColorMap[r.department] || '#1890ff' }}>{r.first_name?.charAt(0)}</Avatar>
                  <div><div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div><div style={{ fontSize: 11, color: '#999' }}>{r.emp_code}</div></div>
                </div>
              )},
              { title: 'Position', dataIndex: 'position' },
              { title: 'Department', dataIndex: 'department', render: (v: string) => <Tag color={deptColorMap[v] || 'blue'}>{v}</Tag> },
              { title: 'Reports To', dataIndex: 'manager_name', render: (v: string) => v || <span style={{ color: '#999' }}>— Top Level —</span> },
              { title: 'Level', dataIndex: 'job_level', render: (v: number) => v ? <Tag color="purple">L{v}</Tag> : '-' },
              { title: 'Band', dataIndex: 'job_band', render: (v: string) => v || '-' },
              { title: 'Cost Centre', dataIndex: 'cost_center', render: (v: string) => v || '-' },
              { title: 'Actions', render: (_: any, r: any) => <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>Edit</Button> },
            ]} />
        )}
      </Card>

      {/* Edit Org Structure Modal */}
      <Modal title={`Edit: ${selected?.first_name} ${selected?.last_name}`} open={editModal} onCancel={() => setEditModal(false)} footer={null} width={500}>
        <Form form={editForm} layout="vertical" onFinish={handleSave}>
          <Form.Item name="reports_to_id" label="Reports To">
            <Select showSearch optionFilterProp="children" allowClear placeholder="Select manager">
              {employees.filter(e => e.id !== selected?.id).map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.position}</Select.Option>)}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="job_level" label="Job Level (1=Entry, 5=Executive)"><Input type="number" min={1} max={10} /></Form.Item></Col>
            <Col span={12}><Form.Item name="job_band" label="Job Band"><Input placeholder="e.g. GL-07, Band C" /></Form.Item></Col>
          </Row>
          <Form.Item name="cost_center" label="Cost Centre"><Input placeholder="e.g. CC-001" /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Save</Button><Button onClick={() => setEditModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};
export default OrgChartPage;
